/**
 * Rule Review Console API client — /api/v1/rule-reviews.
 *
 * Reuses the same Firebase auth interceptor pattern as rulesApi.ts.
 */

import axios from 'axios';
import { auth } from './firebase';
import type {
  CategoryProgress,
  ConsensusSummary,
  InviteReviewerRequest,
  LedgerEntry,
  ProgressSummary,
  ResolveDiscrepancyRequest,
  RuleForReview,
  SubmitReviewRequest,
  SubmitReviewResponse,
} from '../types/ruleReview';

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://est.anterotrail.com/api/v1';

const client = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (err) {
      console.error('[ruleReviewApi] could not get firebase token', err);
    }
  } else if (
    import.meta.env.DEV &&
    import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'
  ) {
    config.headers.Authorization = 'Bearer dev-bypass-token';
  }
  return config;
});

client.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message =
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      error?.message ||
      'Rule review request failed';
    return Promise.reject(new Error(message));
  },
);

export const RuleReviewAPI = {
  // Reviewer endpoints
  listCategories: (): Promise<CategoryProgress[]> =>
    client.get('/rule-reviews/categories'),

  listRules: (params?: {
    category?: string;
    limit?: number;
    offset?: number;
  }): Promise<RuleForReview[]> =>
    client.get('/rule-reviews/rules', { params }),

  submitReview: (
    ruleId: string,
    body: SubmitReviewRequest,
  ): Promise<SubmitReviewResponse> =>
    client.post(`/rule-reviews/${encodeURIComponent(ruleId)}`, body),

  getProgress: (): Promise<ProgressSummary> =>
    client.get('/rule-reviews/progress'),

  // Admin endpoints
  adminConsensus: (): Promise<ConsensusSummary[]> =>
    client.get('/rule-reviews/admin/consensus'),

  adminResolveDiscrepancy: (
    ruleId: string,
    body: ResolveDiscrepancyRequest,
  ): Promise<{ rule_id: string; resolved: boolean }> =>
    client.post(
      `/rule-reviews/admin/discussions/${encodeURIComponent(ruleId)}/resolve`,
      body,
    ),

  adminLedger: (): Promise<LedgerEntry[]> =>
    client.get('/rule-reviews/admin/ledger'),

  adminInviteReviewer: (
    body: InviteReviewerRequest,
  ): Promise<{ user_id: string; email: string; role_granted: string; disciplines: string[] }> =>
    client.post('/rule-reviews/admin/invite', body),
};
